// OSIS - Analize Vânzări
import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  PieChart,
  Download,
  Filter,
  Target,
  DollarSign,
  Users,
  Package,
  Award,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';

const SalesAnalytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('luna');
  const [selectedMetric, setSelectedMetric] = useState('vanzari');

  // Date pentru analize (ultimele 6 luni)
  const vanzariLunare = [
    { luna: 'Aprilie', vanzari: 89500, comenzi: 12, clientiNoi: 4 },
    { luna: 'Mai', vanzari: 123600, comenzi: 18, clientiNoi: 7 },
    { luna: 'Iunie', vanzari: 156800, comenzi: 21, clientiNoi: 5 },
    { luna: 'Iulie', vanzari: 187300, comenzi: 24, clientiNoi: 9 },
    { luna: 'August', vanzari: 201450, comenzi: 28, clientiNoi: 6 },
    { luna: 'Septembrie', vanzari: 127850, comenzi: 19, clientiNoi: 8 }
  ];

  // Analiză pe produse
  const performantaProduse = [
    {
      produs: 'proiecte epoxidic',
      vanzariLuna: '89,500 RON',
      procent: 48,
      comenzi: 145,
      trend: '+12%',
      trendDirection: 'up'
    },
    {
      produs: 'lucrări epoxidic',
      vanzariLuna: '52,200 RON',
      procent: 28,
      comenzi: 87,
      trend: '+8%',
      trendDirection: 'up'
    },
    {
      produs: 'proiecte Aluminiu',
      vanzariLuna: '31,800 RON',
      procent: 17,
      comenzi: 34,
      trend: '+15%',
      trendDirection: 'up'
    },
    {
      produs: 'lucrări Blindate',
      vanzariLuna: '13,350 RON',
      procent: 7,
      comenzi: 12,
      trend: '-3%',
      trendDirection: 'down'
    }
  ];

  // Top clienți
  const topClienti = [
    {
      nume: 'SRL Construct Alpha',
      tip: 'B2B',
      valoare: '156,750 RON',
      comenzi: 8,
      trend: '+24%'
    },
    {
      nume: 'Radu Construct SRL',
      tip: 'B2B',
      valoare: '89,200 RON',
      comenzi: 5,
      trend: '+15%'
    },
    {
      nume: 'Georgescu Ion',
      tip: 'B2C',
      valoare: '31,400 RON',
      comenzi: 3,
      trend: '+8%'
    },
    {
      nume: 'Popescu Maria',
      tip: 'B2C',
      valoare: '18,500 RON',
      comenzi: 2,
      trend: 'nou'
    }
  ];

  // Analiză teritorială
  const performantaTeritoriala = [
    { oras: 'Suceava', vanzari: '145,600 RON', procent: 56, comenzi: 89 },
    { oras: 'Râșnov', vanzari: '52,300 RON', procent: 20, comenzi: 31 },
    { oras: 'Săcele', vanzari: '38,150 RON', procent: 15, comenzi: 24 },
    { oras: 'Predeal', vanzari: '23,800 RON', procent: 9, comenzi: 15 }
  ];

  // KPI-uri comparative
  const kpiComparativ = {
    vanzariActuale: 127850,
    vanzariTrecute: 201450,
    comenziActuale: 19,
    comenziTrecute: 28,
    clientiNoi: 8,
    clientiNoiTrecuti: 6,
    valoareMedie: 6729,
    valoareMedieTrecuta: 7194
  };

  const calculeazaSchimbare = (actual, trecut) => {
    return ((actual - trecut) / trecut * 100).toFixed(1);
  };

  const getColorTrend = (trend) => {
    return trend > 0 ? 'text-green-600' : 'text-red-600';
  };

  const renderChart = () => {
    const maxValue = Math.max(...vanzariLunare.map(item => item[selectedMetric === 'vanzari' ? 'vanzari' : selectedMetric === 'comenzi' ? 'comenzi' : 'clientiNoi']));
    
    return (
      <div className="space-y-3">
        {vanzariLunare.map((item, index) => {
          const value = item[selectedMetric === 'vanzari' ? 'vanzari' : selectedMetric === 'comenzi' ? 'comenzi' : 'clientiNoi'];
          const percentage = (value / maxValue) * 100;
          
          return (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-16 text-sm text-gray-600 dark:text-gray-400">
                {item.luna}
              </div>
              <div className="flex-1">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 h-3 rounded-full transition-all duration-700"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              <div className="w-20 text-sm font-medium text-gray-900 dark:text-white text-right">
                {selectedMetric === 'vanzari' ? 
                  `${Math.round(value/1000)}k` : 
                  value
                }
                {selectedMetric === 'vanzari' && ' RON'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Analize Vânzări OSIS
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Rapoarte și statistici detaliate pentru performanța vânzărilor
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
            >
              <option value="saptamana">Această Săptămână</option>
              <option value="luna">Această Lună</option>
              <option value="trimestru">Acest Trimestru</option>
              <option value="an">Acest An</option>
            </select>
            
            <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              <Download className="w-4 h-4 mr-2" />
              Export Raport
            </button>
          </div>
        </div>

        {/* KPI-uri Comparative */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className={`flex items-center ${getColorTrend(kpiComparativ.vanzariActuale - kpiComparativ.vanzariTrecute)}`}>
                {kpiComparativ.vanzariActuale > kpiComparativ.vanzariTrecute ? 
                  <ArrowUp className="w-4 h-4 mr-1" /> : 
                  <ArrowDown className="w-4 h-4 mr-1" />
                }
                <span className="text-sm font-medium">
                  {calculeazaSchimbare(kpiComparativ.vanzariActuale, kpiComparativ.vanzariTrecute)}%
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {kpiComparativ.vanzariActuale.toLocaleString()} RON
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Vânzări Luna Curentă
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div className={`flex items-center ${getColorTrend(kpiComparativ.comenziActuale - kpiComparativ.comenziTrecute)}`}>
                {kpiComparativ.comenziActuale > kpiComparativ.comenziTrecute ? 
                  <ArrowUp className="w-4 h-4 mr-1" /> : 
                  <ArrowDown className="w-4 h-4 mr-1" />
                }
                <span className="text-sm font-medium">
                  {calculeazaSchimbare(kpiComparativ.comenziActuale, kpiComparativ.comenziTrecute)}%
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {kpiComparativ.comenziActuale}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Comenzi Luna Curentă
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className={`flex items-center ${getColorTrend(kpiComparativ.clientiNoi - kpiComparativ.clientiNoiTrecuti)}`}>
                {kpiComparativ.clientiNoi > kpiComparativ.clientiNoiTrecuti ? 
                  <ArrowUp className="w-4 h-4 mr-1" /> : 
                  <ArrowDown className="w-4 h-4 mr-1" />
                }
                <span className="text-sm font-medium">
                  {calculeazaSchimbare(kpiComparativ.clientiNoi, kpiComparativ.clientiNoiTrecuti)}%
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {kpiComparativ.clientiNoi}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Clienți Noi
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-xl">
                <Target className="w-6 h-6 text-yellow-600" />
              </div>
              <div className={`flex items-center ${getColorTrend(kpiComparativ.valoareMedie - kpiComparativ.valoareMedieTrecuta)}`}>
                {kpiComparativ.valoareMedie > kpiComparativ.valoareMedieTrecuta ? 
                  <ArrowUp className="w-4 h-4 mr-1" /> : 
                  <ArrowDown className="w-4 h-4 mr-1" />
                }
                <span className="text-sm font-medium">
                  {calculeazaSchimbare(kpiComparativ.valoareMedie, kpiComparativ.valoareMedieTrecuta)}%
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {kpiComparativ.valoareMedie.toLocaleString()} RON
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Valoare Medie Comandă
            </p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Grafic Evoluție */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                <BarChart3 className="w-5 h-5 inline mr-2" />
                Evoluție Ultimele 6 Luni
              </h2>
              
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
              >
                <option value="vanzari">Vânzări (RON)</option>
                <option value="comenzi">Comenzi</option>
                <option value="clientiNoi">Clienți Noi</option>
              </select>
            </div>
            
            {renderChart()}
          </Card>

          {/* Performanță pe Produse */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              <PieChart className="w-5 h-5 inline mr-2" />
              Performanță pe Produse
            </h2>
            
            <div className="space-y-4">
              {performantaProduse.map((produs, index) => (
                <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {produs.produs}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Badge variant={produs.trendDirection === 'up' ? 'success' : 'warning'}>
                        {produs.trend}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {produs.vanzariLuna}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {produs.comenzi} comenzi
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${produs.procent}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">{produs.procent}% din total</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Top Clienți */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              <Award className="w-5 h-5 inline mr-2" />
              Top Clienți după Valoare
            </h2>
            
            <div className="space-y-4">
              {topClienti.map((client, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full text-primary-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {client.nume}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {client.comenzi} comenzi • {client.tip}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white">
                      {client.valoare}
                    </div>
                    <Badge 
                      variant={client.trend === 'nou' ? 'primary' : 'success'}
                      size="sm"
                    >
                      {client.trend}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Performanță Teritorială */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Performanță pe Teritorii
            </h2>
            
            <div className="space-y-4">
              {performantaTeritoriala.map((teritoriu, index) => (
                <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {teritoriu.oras}
                    </h3>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {teritoriu.vanzari}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {teritoriu.comenzi} comenzi
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {teritoriu.procent}% din total
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-secondary-500 to-primary-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${teritoriu.procent}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SalesAnalytics;